import { Candle, AnalysisEvent, ConsoleLogMessage } from '../types';
import { MonitorSnapshot, CycleSummaryEvent } from '../types/monitor.types';
import { config } from '../config/api';

function appendToken(url: string): string {
  const token = localStorage.getItem('tradelab_token');
  if (!token) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
}

export class SSEService {
  private candleEventSource: EventSource | null = null;
  private tradeEventSource: EventSource | null = null;
  private analysisEventSource: EventSource | null = null;
  private consoleEventSource: EventSource | null = null;
  private monitorEventSource: EventSource | null = null;
  private onCandleCallback: ((candle: Candle) => void) | null = null;
  private onTradeClosedCallback: (() => void) | null = null;
  private onAnalysisEventCallback: ((event: AnalysisEvent) => void) | null = null;
  private onConsoleLogCallback: ((log: ConsoleLogMessage) => void) | null = null;
  private onMonitorUpdateCallback: ((snapshot: MonitorSnapshot) => void) | null = null;
  private onCycleSummaryCallback: ((summary: CycleSummaryEvent) => void) | null = null;

  connect(instrument: string, resolution: string, onCandle: (candle: Candle) => void) {
    // Close existing connection if any
    this.disconnectCandles();

    this.onCandleCallback = onCandle;

    // Connect to SSE endpoint
    const url = appendToken(`${config.sseBaseUrl}/api/Backtest/candle-stream?epic=${instrument}&resolution=${resolution}`);
    this.candleEventSource = new EventSource(url);

    this.candleEventSource.onmessage = (event) => {
      try {
        const candle: Candle = JSON.parse(event.data);
        if (this.onCandleCallback) {
          this.onCandleCallback(candle);
        }
      } catch (error) {
        console.error('Error parsing SSE candle data:', error);
      }
    };

    this.candleEventSource.onerror = (error) => {
      console.error('SSE candle connection error:', error);
      // Automatically reconnect after 5 seconds
      setTimeout(() => {
        if (this.candleEventSource && this.candleEventSource.readyState === EventSource.CLOSED) {
          this.connect(instrument, resolution, onCandle);
        }
      }, 5000);
    };
  }

  connectTradeEvents(onTradeClosed: () => void) {
    // Close existing trade event connection if any
    this.disconnectTradeEvents();

    this.onTradeClosedCallback = onTradeClosed;

    // Connect to manual trading events endpoint
    const url = appendToken(`${config.sseBaseUrl}/api/ManualTrading/events`);
    this.tradeEventSource = new EventSource(url);

    // Listen for trade-closed events
    this.tradeEventSource.addEventListener('trade-closed', (event: MessageEvent) => {
      try {
        if (this.onTradeClosedCallback) {
          this.onTradeClosedCallback();
        }
      } catch (error) {
        console.error('Error handling trade-closed event:', error);
      }
    });

    this.tradeEventSource.onerror = (error) => {
      console.error('SSE trade events connection error:', error);
      // Automatically reconnect after 5 seconds
      setTimeout(() => {
        if (this.tradeEventSource && this.tradeEventSource.readyState === EventSource.CLOSED) {
          this.connectTradeEvents(onTradeClosed);
        }
      }, 5000);
    };
  }

  disconnectCandles() {
    if (this.candleEventSource) {
      this.candleEventSource.close();
      this.candleEventSource = null;
      this.onCandleCallback = null;
    }
  }

  connectAnalysisStream(onAnalysisEvent: (event: AnalysisEvent) => void) {
    // Close existing analysis event connection if any
    this.disconnectAnalysisStream();

    this.onAnalysisEventCallback = onAnalysisEvent;

    // Connect to analysis stream endpoint
    const url = appendToken(`${config.sseBaseUrl}/api/ManualTrading/analysis-stream`);
    this.analysisEventSource = new EventSource(url);

    // Listen for analysis events
    this.analysisEventSource.addEventListener('analysis', (event: MessageEvent) => {
      try {
        const analysisEvent: AnalysisEvent = JSON.parse(event.data);
        if (this.onAnalysisEventCallback) {
          this.onAnalysisEventCallback(analysisEvent);
        }
      } catch (error) {
        console.error('Error parsing analysis event:', error);
      }
    });

    this.analysisEventSource.onopen = () => {
      console.log('Analysis stream connected');
    };

    this.analysisEventSource.onerror = (error) => {
      console.error('SSE analysis stream connection error:', error);
      // Automatically reconnect after 5 seconds
      setTimeout(() => {
        if (this.analysisEventSource && this.analysisEventSource.readyState === EventSource.CLOSED) {
          this.connectAnalysisStream(onAnalysisEvent);
        }
      }, 5000);
    };
  }

  disconnectTradeEvents() {
    if (this.tradeEventSource) {
      this.tradeEventSource.close();
      this.tradeEventSource = null;
      this.onTradeClosedCallback = null;
    }
  }

  disconnectAnalysisStream() {
    if (this.analysisEventSource) {
      this.analysisEventSource.close();
      this.analysisEventSource = null;
      this.onAnalysisEventCallback = null;
    }
  }

  connectConsoleStream(onConsoleLog: (log: ConsoleLogMessage) => void) {
    // Close existing console event connection if any
    this.disconnectConsoleStream();

    this.onConsoleLogCallback = onConsoleLog;

    // Connect to console stream endpoint
    const url = appendToken(`${config.sseBaseUrl}/api/ManualTrading/console-stream`);
    this.consoleEventSource = new EventSource(url);

    // Listen for console log events
    this.consoleEventSource.addEventListener('log', (event: MessageEvent) => {
      try {
        const logMessage: ConsoleLogMessage = JSON.parse(event.data);
        if (this.onConsoleLogCallback) {
          this.onConsoleLogCallback(logMessage);
        }
      } catch (error) {
        console.error('Error parsing console log:', error);
      }
    });

    // Handle initial connection message and other non-log events
    this.consoleEventSource.onmessage = (event: MessageEvent) => {
      // Initial connection message or other generic messages
      try {
        const data = JSON.parse(event.data);
        if (data.message && data.connectionId) {
          console.log('Console stream connected:', data.connectionId);
        }
      } catch (error) {
        // Ignore parsing errors for non-log messages
      }
    };

    this.consoleEventSource.onopen = () => {
      console.log('Console stream connected');
    };

    this.consoleEventSource.onerror = (error) => {
      console.error('SSE console stream connection error:', error);
      // Automatically reconnect after 5 seconds
      setTimeout(() => {
        if (this.consoleEventSource && this.consoleEventSource.readyState === EventSource.CLOSED) {
          this.connectConsoleStream(onConsoleLog);
        }
      }, 5000);
    };
  }

  disconnectConsoleStream() {
    if (this.consoleEventSource) {
      this.consoleEventSource.close();
      this.consoleEventSource = null;
      this.onConsoleLogCallback = null;
    }
  }

  connectMonitorStream(
    onMonitorUpdate: (snapshot: MonitorSnapshot) => void,
    onCycleSummary?: (summary: CycleSummaryEvent) => void
  ) {
    this.disconnectMonitorStream();

    this.onMonitorUpdateCallback = onMonitorUpdate;
    this.onCycleSummaryCallback = onCycleSummary ?? null;

    const url = appendToken(`${config.sseBaseUrl}/api/PositionMonitor/status-stream`);
    this.monitorEventSource = new EventSource(url);

    this.monitorEventSource.addEventListener('monitor-update', (event: MessageEvent) => {
      try {
        const snapshot: MonitorSnapshot = JSON.parse(event.data);
        if (this.onMonitorUpdateCallback) {
          this.onMonitorUpdateCallback(snapshot);
        }
      } catch (error) {
        console.error('Error parsing monitor update:', error);
      }
    });

    this.monitorEventSource.addEventListener('cycle-summary', (event: MessageEvent) => {
      try {
        const summary: CycleSummaryEvent = JSON.parse(event.data);
        if (this.onCycleSummaryCallback) {
          this.onCycleSummaryCallback(summary);
        }
      } catch (error) {
        console.error('Error parsing cycle-summary:', error);
      }
    });

    this.monitorEventSource.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.message && data.connectionId) {
          console.log('Monitor stream connected:', data.connectionId);
        }
      } catch (error) {
        // Ignore parsing errors for non-monitor messages
      }
    };

    this.monitorEventSource.onerror = (error) => {
      console.error('SSE monitor stream connection error:', error);
      setTimeout(() => {
        if (this.monitorEventSource && this.monitorEventSource.readyState === EventSource.CLOSED) {
          this.connectMonitorStream(onMonitorUpdate);
        }
      }, 5000);
    };
  }

  disconnectMonitorStream() {
    if (this.monitorEventSource) {
      this.monitorEventSource.close();
      this.monitorEventSource = null;
      this.onMonitorUpdateCallback = null;
      this.onCycleSummaryCallback = null;
    }
  }

  disconnect() {
    this.disconnectCandles();
    this.disconnectTradeEvents();
    this.disconnectAnalysisStream();
    this.disconnectConsoleStream();
    this.disconnectMonitorStream();
  }

  isConnected(): boolean {
    return this.candleEventSource !== null && this.candleEventSource.readyState === EventSource.OPEN;
  }

  isTradeEventsConnected(): boolean {
    return this.tradeEventSource !== null && this.tradeEventSource.readyState === EventSource.OPEN;
  }

  isAnalysisStreamConnected(): boolean {
    return this.analysisEventSource !== null && this.analysisEventSource.readyState === EventSource.OPEN;
  }

  isConsoleStreamConnected(): boolean {
    return this.consoleEventSource !== null && this.consoleEventSource.readyState === EventSource.OPEN;
  }

  isMonitorStreamConnected(): boolean {
    return this.monitorEventSource !== null && this.monitorEventSource.readyState === EventSource.OPEN;
  }
}

export const sseService = new SSEService();
